
import React, { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove, update } from 'firebase/database';
import { db } from '../../firebase';
import { Product } from '../../types';
import { Plus, Trash2, Edit2, X, Upload, Check, ImageIcon } from 'lucide-react';
import { useApp } from '../../App';

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { categories } = useApp();

  const initialFormState = {
    name: '',
    description: '',
    price: 0,
    oldPrice: 0,
    category: '',
    brand: '',
    images: [] as string[],
    specs: {} as Record<string, string>,
    isNew: false,
    isFeatured: false,
    stock: 0
  };

  const [formData, setFormData] = useState(initialFormState);
  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');

  useEffect(() => {
    const productsRef = ref(db, 'products');
    const unsubscribe = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data).map(([id, val]: [string, any]) => ({
          ...val,
          id,
          images: val.images || [], 
          specs: val.specs || {}    
        })) as Product[];
        setProducts(list);
      } else {
        setProducts([]);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) { 
      alert("Görsel boyutu 2MB'den küçük olmalıdır.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), base64String]
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanSpecs = Object.fromEntries(
      Object.entries(formData.specs || {}).filter(([_, v]) => v !== '')
    );
    
    const finalData = { 
      ...formData, 
      specs: cleanSpecs, 
      price: Number(formData.price), 
      oldPrice: Number(formData.oldPrice),
      stock: Number(formData.stock),
      images: formData.images || []
    };

    try {
      if (editingId) {
        await update(ref(db, `products/${editingId}`), finalData);
      } else {
        const newRef = push(ref(db, 'products'));
        await set(newRef, { ...finalData, id: newRef.key });
      }

      setIsModalOpen(false);
      setFormData(initialFormState);
      setEditingId(null);
    } catch (error) {
      console.error("Kaydetme hatası:", error);
      alert("Veri kaydedilirken bir hata oluştu.");
    }
  };

  const startEdit = (p: Product) => {
    setFormData({
      name: p.name || '',
      description: p.description || '',
      price: p.price || 0,
      oldPrice: p.oldPrice || 0,
      category: p.category || '',
      brand: p.brand || '',
      images: p.images || [],
      specs: p.specs || {},
      isNew: !!p.isNew,
      isFeatured: !!p.isFeatured,
      stock: p.stock || 0
    });
    setEditingId(p.id);
    setIsModalOpen(true);
  };

  const deleteProduct = (id: string) => {
    if (window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
      remove(ref(db, `products/${id}`));
    }
  };

  const addSpec = () => {
    if (specKey && specValue) {
      setFormData(prev => ({ ...prev, specs: { ...(prev.specs || {}), [specKey]: specValue } }));
      setSpecKey('');
      setSpecValue('');
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-black text-stone-900">Ürün Yönetimi</h1>
          <p className="text-stone-500">Katalogdaki tüm ürünleri buradan düzenleyebilirsiniz.</p>
        </div>
        <button 
          onClick={() => { setFormData(initialFormState); setEditingId(null); setIsModalOpen(true); }}
          className="bg-amber-800 text-white px-6 py-3 rounded-2xl font-bold flex items-center space-x-2 shadow-lg shadow-amber-900/20"
        >
          <Plus size={20} />
          <span>Yeni Ürün Ekle</span>
        </button>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-stone-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-stone-50 border-b border-stone-100 text-stone-400 text-xs font-black uppercase tracking-widest">
            <tr>
              <th className="px-6 py-4">Görsel</th>
              <th className="px-6 py-4">Ürün Adı</th>
              <th className="px-6 py-4">Kategori</th>
              <th className="px-6 py-4">Fiyat</th>
              <th className="px-6 py-4">Stok</th>
              <th className="px-6 py-4 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {products.map(p => (
              <tr key={p.id} className="hover:bg-stone-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="w-12 h-12 rounded-xl bg-stone-100 p-1 flex items-center justify-center overflow-hidden">
                    {p.images && p.images.length > 0 ? (
                      <img 
                        src={p.images[0]} 
                        className="w-full h-full object-cover rounded-lg" 
                        alt={p.name}
                      />
                    ) : (
                      <ImageIcon className="text-stone-300" size={20} />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="font-bold text-stone-900">{p.name || 'İsimsiz Ürün'}</div>
                  <div className="text-[10px] text-stone-400 font-black uppercase tracking-wider">{p.brand || 'Lumina'}</div>
                </td>
                <td className="px-6 py-4">
                  <span className="bg-stone-100 text-stone-600 text-[10px] font-black uppercase px-2 py-1 rounded-full">{p.category || 'Genel'}</span>
                </td>
                <td className="px-6 py-4 font-black text-amber-800">₺{p.price?.toLocaleString() || 0}</td>
                <td className="px-6 py-4 text-stone-500 text-sm font-medium">{p.stock || 0} Adet</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <button onClick={() => startEdit(p)} className="p-2 text-stone-400 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-all"><Edit2 size={18} /></button>
                    <button onClick={() => deleteProduct(p.id)} className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] flex flex-col overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-xl font-black text-stone-900"> {editingId ? 'Ürünü Düzenle' : 'Yeni Ürün'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-stone-400 hover:text-stone-600"><X size={24} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-stone-700 mb-2">Ürün Adı</label>
                    <input 
                      type="text" required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 placeholder-stone-400"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-stone-700 mb-2">Marka</label>
                      <input 
                        type="text" required
                        value={formData.brand}
                        onChange={(e) => setFormData({...formData, brand: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900 placeholder-stone-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-stone-700 mb-2">Kategori</label>
                      <select 
                        required
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                      >
                        <option value="">Seçiniz</option>
                        {categories.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-stone-700 mb-2">Stok</label>
                      <input 
                        type="number" required
                        value={formData.stock}
                        onChange={(e) => setFormData({...formData, stock: Number(e.target.value)})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-stone-700 mb-2">Fiyat (TL)</label>
                      <input 
                        type="number" required
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: Number(e.target.value)})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-stone-700 mb-2">Eski Fiyat</label>
                      <input 
                        type="number"
                        value={formData.oldPrice}
                        onChange={(e) => setFormData({...formData, oldPrice: Number(e.target.value)})}
                        className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none text-stone-900"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-stone-700 mb-2">Açıklama</label>
                    <textarea 
                      required rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full bg-stone-50 border border-stone-100 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-amber-500 outline-none resize-none text-stone-900 placeholder-stone-400"
                    ></textarea>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-bold text-stone-700 mb-2">Ürün Görselleri (Base64)</label>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {(formData.images || []).map((img, idx) => (
                        <div key={idx} className="relative aspect-square rounded-xl bg-stone-50 p-1 group border border-stone-100">
                          <img src={img} className="w-full h-full object-cover rounded-lg" alt="Ürün" />
                          <button 
                            type="button"
                            onClick={() => setFormData({...formData, images: formData.images.filter((_, i) => i !== idx)})}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <label className="aspect-square border-2 border-dashed border-stone-200 rounded-xl flex items-center justify-center text-stone-400 hover:text-amber-800 hover:border-amber-500 cursor-pointer transition-all">
                        <Upload size={20} />
                        <input type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-stone-700 mb-2">Teknik Özellikler</label>
                    <div className="space-y-2 mb-4">
                      {Object.entries(formData.specs || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between bg-stone-50 p-2 rounded-xl text-xs">
                          <span className="font-bold text-stone-900">{k}:</span>
                          <span className="text-stone-600">{v}</span>
                          <button 
                            type="button" 
                            onClick={() => {
                              const newSpecs = {...formData.specs};
                              delete newSpecs[k];
                              setFormData({...formData, specs: newSpecs});
                            }}
                            className="text-red-400 ml-2"
                          ><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input 
                        placeholder="Anahtar"
                        value={specKey}
                        onChange={(e) => setSpecKey(e.target.value)}
                        className="flex-1 bg-stone-50 border border-stone-100 rounded-xl py-2 px-3 text-sm text-stone-900"
                      />
                      <input 
                        placeholder="Değer"
                        value={specValue}
                        onChange={(e) => setSpecValue(e.target.value)}
                        className="flex-1 bg-stone-50 border border-stone-100 rounded-xl py-2 px-3 text-sm text-stone-900"
                      />
                      <button 
                        type="button"
                        onClick={addSpec}
                        className="bg-stone-900 text-white p-2 rounded-xl"
                      ><Plus size={20} /></button>
                    </div>
                  </div>

                  <div className="flex space-x-6">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.isNew} 
                        onChange={(e) => setFormData({...formData, isNew: e.target.checked})}
                        className="w-5 h-5 rounded-lg border-stone-300 text-amber-800 focus:ring-amber-500" 
                      />
                      <span className="text-sm font-bold text-stone-700">Yeni Ürün</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.isFeatured} 
                        onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                        className="w-5 h-5 rounded-lg border-stone-300 text-amber-800 focus:ring-amber-500" 
                      />
                      <span className="text-sm font-bold text-stone-700">Öne Çıkar</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-stone-100 flex gap-4">
                <button 
                  type="submit"
                  className="flex-1 bg-amber-800 text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-2 hover:bg-amber-900 transition-all shadow-xl shadow-amber-900/20"
                >
                  <Check size={20} />
                  <span>{editingId ? 'Güncelle' : 'Kaydet'}</span>
                </button>
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 border border-stone-200 text-stone-600 rounded-2xl font-bold hover:bg-stone-50 transition-all"
                >
                  Vazgeç
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
